import moment from 'moment';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Input, Range, useQueryFetch } from 'sonata-react-components';

import { getCountryLabel } from '../../MultiFlightMap.helpers';
import { useMapPopover } from '../MapPopover/MapPopover.context';
import { resolveLocationInfo } from '../MapView/map.helpers';

import type { LocationInfo } from '../MapView/map.helpers';

import './shapeConfirmation.scss';

type Props = {
  center: [number, number];
  initialRadiusInKm: number;
  onCancel: () => void;
  onConfirm: (data: { name: string; radiusInKm: number }) => void;
};

type State = {
  defaultName: string;
  name: string;
  radiusInMeters: number;
};

const RADIUS_MIN_METERS = 20;

function clampRadius(paramMeters: number): number {
  const meters = Number(paramMeters);
  if (Number.isNaN(meters)) {
    return RADIUS_MIN_METERS;
  }
  return Math.max(meters, RADIUS_MIN_METERS);
}

function formatLatLng(value: number): string {
  return value.toFixed(5);
}

export function CircleConfirmation({ center, initialRadiusInKm, onCancel, onConfirm }: Props) {
  const { formatMessage } = useIntl();
  const { closeMapPopover } = useMapPopover();

  const [state, setState] = useState<State>(() => ({
    defaultName: `New area ${moment().format('YYYY-MM-DD hh:mm:ss')}`,
    name: '',
    radiusInMeters: clampRadius(Math.round(initialRadiusInKm * 1000))
  }));

  const hasUserEditedNameRef = useRef(false);

  const { data, isLoading: isResolvingLocation } = useQueryFetch<LocationInfo>(
    ['multiFlightMap.shapeConfirmation.locationInfo', center[0], center[1]],
    () => resolveLocationInfo(center[1], center[0]),
    { staleTime: Number.POSITIVE_INFINITY }
  );

  // Derive displayName from current state — no effect, no re-keying needed.
  // `||` (not `??`): `resolveLocationInfo` returns placeName: '' when Mapbox
  // has no address feature for these coordinates (or the request failed), and
  // we want to fall back to the dated default in that case, not display an
  // empty input.
  const displayName = hasUserEditedNameRef.current ? state.name : data?.placeName || state.defaultName;
  const handleNameChange = (value: string | FileList | null) => {
    hasUserEditedNameRef.current = true;
    if (typeof value === 'string') {
      setState(prev => ({ ...prev, name: value }));
    }
  };

  const handleRadiusChange = (newValue: number | number[]) => {
    const next = Array.isArray(newValue) ? newValue[0] : newValue;
    setState(prev => ({ ...prev, radiusInMeters: clampRadius(Number(next)) }));
  };

  const handleConfirm = () => {
    const trimmedName = state.name.trim() || state.defaultName;
    closeMapPopover();
    onConfirm({
      name: trimmedName,
      radiusInKm: state.radiusInMeters / 1000
    });
  };

  const handleCancel = () => {
    closeMapPopover();
    onCancel();
  };

  const country = data?.country ?? '';
  const countryLabel = isResolvingLocation
    ? formatMessage({
        defaultMessage: 'Detecting…',
        id: 'multiFlightLocations.confirmation.country.loading'
      })
    : country
      ? getCountryLabel(country)
      : formatMessage({
          defaultMessage: 'N/A',
          id: 'common.notAvailableShort'
        });

  return (
    <div className="shapeConfirmation">
      <Input
        className="shapeConfirmation__input"
        id="circleConfirmation-name"
        label={formatMessage({
          defaultMessage: 'Name',
          id: 'common.name'
        })}
        name="circleConfirmation-name"
        notFloatingPlaceholder
        onChange={handleNameChange}
        shape="squareUI"
        tiny
        type="text"
        value={displayName}
      />
      <div className="shapeConfirmation__radius">
        <Range
          className="shapeConfirmation__range"
          id="shapeConfirmation__range"
          min={RADIUS_MIN_METERS}
          onChange={handleRadiusChange}
          type="slider"
          value={state.radiusInMeters}
        >
          <Input
            className="shapeConfirmation__radiusInput"
            id="shapeConfirmation-radius"
            inlineLabel
            inputAdditionalProps={{ min: RADIUS_MIN_METERS }}
            label={formatMessage({
              defaultMessage: 'Radius',
              id: 'common.radius'
            })}
            name="shapeConfirmation-radius"
            onChange={value => handleRadiusChange(Number(value))}
            shape="squareUI"
            tiny
            type="number"
            value={state.radiusInMeters}
          />
        </Range>
      </div>
      <div className="shapeConfirmation__row">
        <span className="shapeConfirmation__coordinatesContainer">
          <span className="shapeConfirmation__label">
            {formatMessage({
              defaultMessage: 'Coordinates:',
              id: 'mapAreaConfirmation.coordinates'
            })}
          </span>
          <i className="shapeConfirmation__icon fa-solid fa-map-pin" />
          <span className="shapeConfirmation__value">
            {formatLatLng(center[1])}, {formatLatLng(center[0])}
          </span>
        </span>
      </div>
      <div className="shapeConfirmation__row">
        <span className="shapeConfirmation__label">
          {formatMessage({
            defaultMessage: 'Country:',
            id: 'mapAreaConfirmation.country'
          })}
        </span>
        <span className="shapeConfirmation__value">{countryLabel}</span>
      </div>
      <div className="shapeConfirmation__buttonsContainer">
        <ButtonUI
          icon="fal fa-xmark"
          onClick={handleCancel}
          size="ultraSmall"
          text={formatMessage({
            defaultMessage: 'Cancel',
            id: 'common.cancel'
          })}
          type="tertiary"
        />
        <ButtonUI
          className="shapeConfirmation__acceptButton"
          icon="fa-regular fa-floppy-disk"
          onClick={handleConfirm}
          size="ultraSmall"
          text={formatMessage({
            defaultMessage: 'Add Area',
            id: 'mapAreaConfirmation.addButton'
          })}
          type="primary"
        />
      </div>
    </div>
  );
}
